// src/components/OralExamComponent/OralExamComponent.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './OralExamComponent.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faClock, faInfoCircle, faCheckCircle, faVolumeUp, faStop, faArrowLeft, faArrowRight, faSpinner, faPlayCircle, faPauseCircle } from '@fortawesome/free-solid-svg-icons';
/* import AWS from 'aws-sdk'; */

// Mock useRoute and useNavigation for web demo
const useRoute = () => {
    // Mock route.params with some dummy data for demonstration
    return {
        params: {
            type: 'complete', // or 'task1', 'task2', 'task3'
            sessionData: {
                introductions: {
                    introduction: { audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
                    transitions: {
                        task1_to_task2: { audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
                        task2_to_task3: { audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' },
                    },
                },
                questions: [
                    { questionNumber: 1, text: 'Décrivez-vous et parlez de votre parcours.', audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3' },
                    { questionNumber: 2, text: 'Vous souhaitez organiser un événement. Demandez à votre ami(e) des informations sur les lieux et la disponibilité.', audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3' },
                    { questionNumber: 3, text: 'Donnez votre opinion sur l\'impact de la technologie sur la vie quotidienne.', audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3' },
                ],
            },
        },
    };
};

const useNavigate = () => {
    return {
        goBack: () => { console.log('Navigating back...'); alert('Simulating goBack()'); },
        navigate: (path, params) => { console.log(`Navigating to: ${path}`, params); alert(`Simulating navigation to ${path}`); }
    };
};

// Simplified Alert and Toast for web
const showAlertDialog = (title, message, buttons) => {
    alert(`${title}\n${message}`);
};

const showToast = (message, type = 'info') => {
    console.log(`Toast (${type}): ${message}`);
    // In a real app, use a toast library (e.g., react-toastify)
    const toastDiv = document.createElement('div');
    toastDiv.textContent = message;
    toastDiv.className = styles['web-toast'];
    if (type === 'error') toastDiv.classList.add(styles['web-toast-error']);
    if (type === 'success') toastDiv.classList.add(styles['web-toast-success']);
    document.body.appendChild(toastDiv);
    setTimeout(() => {
        toastDiv.remove();
    }, 3000);
};

// Mock AWS S3 Configuration (Highly Insecure for Production!)
// In a real app, you would use a backend to generate pre-signed URLs
// and manage credentials securely.
/* AWS.config.update({
    accessKeyId: 'AKIAVIVDHMRX5UMZD47V', // Replace with your actual credentials or environment variables
    secretAccessKey: 'g3448VXujQRFuaJrXfJ0jNxcO7Dai6qbPhanTz1z', // Replace with your actual credentials or environment variables
    region: 'us-east-2',
});
 */
/* const s3 = new AWS.S3({
    signatureVersion: 'v4',
}); */

const BUCKET_NAME = 'tcftef-agent-audios'; // Replace with your S3 bucket name

// Define audio context for Web Audio API for recording.
// For playback, standard HTML Audio element is sufficient.
const audioContext = new (window.AudioContext || window.webkitAudioContext)();

const ExamScreenExpressionOrale = () => {
    const route = useRoute();
    const navigation = useNavigate();
    const { type, sessionData } = route.params;

    // States for exam management
    const [currentStep, setCurrentStep] = useState('introduction');
    const [currentTask, setCurrentTask] = useState(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPreparing, setIsPreparing] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [canSkipIntro, setCanSkipIntro] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStep, setSubmissionStep] = useState('');
    const [candidateResponses, setCandidateResponses] = useState([]);
    const [taskProgress, setTaskProgress] = useState(0);

    // References for audio, recording, and timers
    const audioRef = useRef(null); // For HTMLAudioElement
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const pulseAnimationInterval = useRef(null);
    const waveAnimationInterval = useRef(null);

    // Configuration des durées selon les tâches (in seconds)
    const taskDurations = {
        task1: 2 * 60, // 2 minutes
        task2: 3.5 * 60, // 3.5 minutes
        task3: 4.5 * 60 // 4.5 minutes
    };
    const preparationDuration = 2 * 60; // 2 minutes

    // --- Animations (using CSS classes for simplicity, could be React Spring/Framer Motion) ---
    const startPulseAnimation = useCallback(() => {
        pulseAnimationInterval.current = setInterval(() => {
            // Apply/remove CSS class for pulse animation
            // This is handled by CSS class .exam-component-recording-icon.pulsing
        }, 1600); // Interval matches CSS animation duration
    }, []);

    const stopPulseAnimation = useCallback(() => {
        if (pulseAnimationInterval.current) {
            clearInterval(pulseAnimationInterval.current);
            pulseAnimationInterval.current = null;
        }
    }, []);

    const startWaveAnimation = useCallback(() => {
        // This is handled by CSS animations on .exam-component-wave-bars
        // No explicit JS interval needed if CSS is purely animating
    }, []);

    const stopWaveAnimation = useCallback(() => {
        // Stop is implied by removing the isAudioPlaying state
    }, []);

    const updateProgressAnimation = useCallback((progress) => {
        // This will be handled by setting a CSS variable or direct style
        const progressBarFill = document.querySelector(`.${styles['exam-component-progress-bar-fill']}`);
        if (progressBarFill) {
            progressBarFill.style.width = `${progress * 100}%`;
        }
        const progressRingFill = document.querySelector(`.${styles['exam-component-progress-ring-fill']}`);
        if (progressRingFill) {
            // For a circular progress bar, you'd typically use SVG stroke-dashoffset or similar
            // Here, we'll just rotate it as a simple visual cue, like RN's Animated.View
            // A perfect circular progress bar is more complex with pure CSS/HTML
            progressRingFill.style.transform = `rotate(${progress * 360}deg)`;
        }
    }, []);

    // Cleanup resources
    const cleanupResources = useCallback(async () => {
        console.log('🧹 Cleaning up resources...');
        stopPulseAnimation();
        stopWaveAnimation();

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
            audioRef.current.load();
            audioRef.current = null;
            setIsAudioPlaying(false);
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try {
                mediaRecorderRef.current.stop();
            } catch (e) {
                console.warn('MediaRecorder was already stopped or in error state:', e);
            }
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setIsRecording(false);
        setIsPreparing(false);
        setTimeRemaining(0);
        audioChunksRef.current = [];
    }, [stopPulseAnimation, stopWaveAnimation]);

    // Handle browser tab/window focus/blur (replaces AppState)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // User switched tabs or minimized
                console.log('Tab hidden, cleaning up resources...');
                cleanupResources();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', cleanupResources); // Clean up on tab close/refresh

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', cleanupResources);
            cleanupResources(); // Final cleanup on unmount
        };
    }, [cleanupResources]);

    // Play audio with visual feedback
    const playAudio = useCallback(async (audioUrl, onFinish) => {
        try {
            console.log('🎵 Playing audio:', audioUrl);
            await cleanupResources(); // Clean up previous audio/recording

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current.load();
            }
            audioRef.current = new Audio(audioUrl);
            audioRef.current.volume = 0.8; // Example volume control

            audioRef.current.onended = () => {
                setIsAudioPlaying(false);
                stopWaveAnimation();
                if (onFinish) onFinish();
            };

            audioRef.current.onerror = (e) => {
                console.error('Audio playback error:', e);
                showToast('Erreur de lecture audio. Veuillez vérifier votre connexion.', 'error');
                setIsAudioPlaying(false);
                stopWaveAnimation();
                if (onFinish) onFinish(); // Continue even on error
            };

            await audioRef.current.play();
            setIsAudioPlaying(true);
            startWaveAnimation();
        } catch (error) {
            console.error('Error playing audio:', error);
            showToast('Impossible de lire l\'audio.', 'error');
            setIsAudioPlaying(false);
            if (onFinish) onFinish();
        }
    }, [cleanupResources, startWaveAnimation, stopWaveAnimation]);

    // Stop audio
    const stopAudio = useCallback(async () => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current.load(); // Unload audio
                audioRef.current = null;
            }
            setIsAudioPlaying(false);
            stopWaveAnimation();
        } catch (error) {
            console.log('Error stopping audio:', error.message);
            setIsAudioPlaying(false);
        }
    }, [stopWaveAnimation]);

    // Save candidate response
    const saveCandidateResponse = useCallback(async (taskNum, audioBlob) => {
        console.log('💾 Saving response for task:', taskNum, 'Blob size:', audioBlob.size);

        if (!taskNum || !audioBlob) {
            console.warn('⚠️ Missing data to save response:', { taskNum, audioBlob });
            return null;
        }

        const taskNameMap = {
            task1: 'Entretien dirigé',
            task2: 'Exercice interactif',
            task3: 'Expression d\'un point de vue'
        };
        const taskName = type === 'complete' ? taskNameMap[taskNum] : taskNameMap[type];

        const response = {
            taskNumber: taskNum,
            taskName: taskName,
            examType: type,
            duration: formatTime(taskDurations[taskNum] || 0),
            userRecordingBlob: audioBlob, // Store the Blob directly for upload
            audioUrl: null, // This will be filled after S3 upload
            timestamp: new Date().toISOString()
        };

        console.log('📝 Response created:', response);

        setCandidateResponses(prev => {
            const newResponses = [...prev, response];
            console.log('📊 New total responses:', newResponses.length);
            console.log('📊 All responses:', newResponses);
            return newResponses;
        });

        return response;
    }, [type, taskDurations]);

    // Start recording
    const startRecording = useCallback(async (duration) => {
        try {
            console.log('🎙️ Starting recording for', duration, 'seconds');
            audioChunksRef.current = []; // Clear previous chunks

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorderRef.current.mimeType });
                console.log('Recording stopped, Blob created:', audioBlob);
                mediaRecorderRef.current = null; // Clear recorder reference
                stream.getTracks().forEach(track => track.stop()); // Stop microphone access

                // Pass the Blob to saveCandidateResponse
                await saveCandidateResponse(currentTask, audioBlob);
                handleRecordingComplete();
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setTimeRemaining(duration);
            stopPulseAnimation(); // Ensure it's stopped before starting new one
            startPulseAnimation();
            document.querySelector(`.${styles['exam-component-recording-icon']}`).classList.add(styles['pulsing']);


            console.log('✅ Recording started successfully');

            // Timer for recording
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(() => {
                setTimeRemaining((prev) => {
                    const newTime = prev - 1;
                    const progress = 1 - (newTime / duration);
                    updateProgressAnimation(progress);

                    if (newTime <= 0) {
                        console.log('⏰ Time elapsed, automatically stopping recording');
                        stopRecording(); // Automatically stop when time is up
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);

        } catch (error) {
            console.error('❌ Error starting recording:', error);
            showToast('Impossible de démarrer l\'enregistrement. Vérifiez votre microphone.', 'error');
            setIsRecording(false);
        }
    }, [saveCandidateResponse, currentTask, startPulseAnimation, stopPulseAnimation, updateProgressAnimation]);

    // Stop recording
    const stopRecording = useCallback(async () => {
        try {
            console.log('🛑 Stopping recording...');
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop(); // This triggers onstop event handler
            } else {
                console.warn('No active recording to stop.');
                // If recording was already stopped or never started, proceed with cleanup
                handleRecordingComplete(); // Proceed to next step
            }

            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            setIsRecording(false);
            setTimeRemaining(0);
            stopPulseAnimation();
            document.querySelector(`.${styles['exam-component-recording-icon']}`).classList.remove(styles['pulsing']);

            // handleRecordingComplete is now called by mediaRecorderRef.current.onstop

        } catch (error) {
            console.error('❌ Error stopping recording:', error);
            showToast('Erreur lors de l\'arrêt de l\'enregistrement.', 'error');
        }
    }, [handleRecordingComplete, stopPulseAnimation]);

    // Start preparation (only for task 2)
    const startPreparation = useCallback(() => {
        console.log('📝 Starting preparation');
        setIsPreparing(true);
        setTimeRemaining(preparationDuration);

        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeRemaining((prev) => {
                const newTime = prev - 1;
                const progress = 1 - (newTime / preparationDuration);
                updateProgressAnimation(progress);

                if (newTime <= 0) {
                    console.log('⏰ End of preparation, starting recording');
                    setIsPreparing(false);
                    clearInterval(timerRef.current);
                    startRecording(taskDurations.task2);
                    return 0;
                }
                return newTime;
            });
        }, 1000);
    }, [startRecording, updateProgressAnimation, taskDurations.task2]);

    // Skip preparation time
    const skipPreparation = useCallback(() => {
        console.log('⏭️ Preparation skipped');
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsPreparing(false);
        setTimeRemaining(0);
        startRecording(taskDurations.task2);
    }, [startRecording, taskDurations.task2]);

    // Handle end of recording
    const handleRecordingComplete = useCallback(() => {
        console.log('🎬 Recording complete for task:', currentTask);

        if (type === 'complete') {
            if (currentTask === 'task1') {
                setTaskProgress(33);
                console.log('➡️ Transition to task 2');
                playTransition('task1_to_task2', () => {
                    setCurrentTask('task2');
                    setCurrentStep('task2');
                });
            } else if (currentTask === 'task2') {
                setTaskProgress(66);
                console.log('➡️ Transition to task 3');
                playTransition('task2_to_task3', () => {
                    setCurrentTask('task3');
                    setCurrentStep('task3');
                });
            } else if (currentTask === 'task3') {
                setTaskProgress(100);
                console.log('🏁 End of complete exam');
                handleExamComplete();
            }
        } else {
            console.log('🏁 End of single task exam');
            handleExamComplete();
        }
    }, [currentTask, type, playTransition]); // Add playTransition to deps

    // Manage exam completion with upload
    const handleExamComplete = useCallback(async () => {
        console.log('🏁 Finalizing exam...');
        setIsSubmitting(true);
        setSubmissionStep('Finalisation de l\'examen...');

        try {
            // Give a short delay to ensure last state update is processed
            await new Promise(resolve => setTimeout(resolve, 500));

            let finalResponses = [];
            // Use an immediate state snapshot or a ref if candidateResponses can lag.
            // For now, rely on the useEffect for candidateResponses to catch up.
            // A more robust way might be to pass the recording Blob directly to upload.
            finalResponses = candidateResponses; // Direct reference at time of call

            if (finalResponses.length === 0) {
                console.warn('⚠️ No responses found, trying with current ref');
                // You might need a more sophisticated way to get the *latest* state
                // if `candidateResponses` isn't fully updated by the time this runs.
                // For a web app, a ref might work: const currentResponsesRef = useRef([]);
                // then update currentResponsesRef.current.push(...) in saveCandidateResponse
                // and access currentResponsesRef.current here.
            }

            // Fallback: If still no responses, alert the user and offer to proceed without upload
            if (finalResponses.length === 0 || finalResponses.every(r => !r.userRecordingBlob)) {
                showAlertDialog(
                    'Aucun enregistrement',
                    'Aucun enregistrement n\'a été détecté. Voulez-vous quand même terminer l\'examen ?',
                    [
                        { text: 'Annuler', style: 'cancel', onPress: () => setIsSubmitting(false) },
                        {
                            text: 'Terminer', onPress: () => {
                                setIsSubmitting(false);
                                setCurrentStep('completed');
                            }
                        }
                    ]
                );
                return;
            }

            console.log('📤 Starting upload with', finalResponses.length, 'responses');
            const updatedResponses = await uploadAudiosToS3(finalResponses);
            console.log('✅ Candidate responses with S3 URLs:', updatedResponses);

            setTimeout(() => {
                setIsSubmitting(false);
                setCurrentStep('completed');
            }, 1500);

        } catch (error) {
            console.error('❌ Error in handleExamComplete:', error);
            setIsSubmitting(false);
            setCurrentStep('completed');
            showToast('Erreur lors de la finalisation de l\'examen.', 'error');
        }
    }, [candidateResponses, uploadAudiosToS3]);

    // Play a transition audio
    const playTransition = useCallback((transitionType, onFinish) => {
        const transitionAudio = sessionData.introductions?.transitions?.[transitionType];
        if (transitionAudio?.audioUrl) {
            playAudio(transitionAudio.audioUrl, onFinish);
        } else {
            console.log(`No transition audio for ${transitionType}, proceeding directly.`);
            onFinish();
        }
    }, [sessionData, playAudio]);

    // Start introduction
    const startIntroduction = useCallback(() => {
        const introAudio = sessionData.introductions?.introduction?.audioUrl;
        if (introAudio) {
            setCanSkipIntro(true);
            playAudio(introAudio, () => {
                setCanSkipIntro(false);
                startFirstTask();
            });
        } else {
            console.warn('No introduction audio found, starting first task directly.');
            startFirstTask();
        }
    }, [sessionData, playAudio, startFirstTask]);

    // Skip introduction
    const skipIntroduction = useCallback(() => {
        stopAudio();
        setCanSkipIntro(false);
        startFirstTask();
    }, [stopAudio, startFirstTask]);

    // Start the first task
    const startFirstTask = useCallback(() => {
        if (type === 'complete') {
            console.log('🎯 Starting task 1 (complete exam)');
            setCurrentTask('task1');
            setCurrentStep('task1');
        } else {
            console.log('🎯 Starting single task:', type);
            setCurrentTask(type);
            setCurrentStep(type);
        }
    }, [type]);

    // Start a specific task (plays question audio then starts recording/preparation)
    const startTask = useCallback((taskType) => {
        console.log('🎯 Starting task:', taskType);

        const questionNumber = taskType === 'task1' ? 1 : taskType === 'task2' ? 2 : 3;
        const question = sessionData.questions?.find(q => q.questionNumber === questionNumber);

        if (question?.audioUrl) {
            console.log('🎵 Playing question audio for', taskType);
            playAudio(question.audioUrl, () => {
                if (taskType === 'task2') {
                    console.log('📝 Starting preparation for task 2');
                    startPreparation();
                } else {
                    console.log('🎙️ Starting direct recording for', taskType);
                    startRecording(taskDurations[taskType]);
                }
            });
        } else {
            console.warn('⚠️ No audio for question, starting directly.');
            if (taskType === 'task2') {
                startPreparation();
            } else {
                startRecording(taskDurations[taskType]);
            }
        }
    }, [sessionData, playAudio, startPreparation, startRecording, taskDurations]);

    // Effect to manage step changes
    useEffect(() => {
        if (currentStep === 'task1' || currentStep === 'task2' || currentStep === 'task3') {
            startTask(currentStep);
        }
    }, [currentStep, startTask]);

    // Format time for display
    const formatTime = (seconds) => {
        if (isNaN(seconds) || seconds < 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Generate unique ID (for S3 key)
    const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

    // Upload single audio to S3
    const uploadSingleAudioToS3 = useCallback(async (audioBlob, taskNumber) => {
        try {
            console.log(`📤 Uploading audio for task ${taskNumber}:`, audioBlob.type, audioBlob.size, 'bytes');
            setSubmissionStep(`Upload de l'audio tâche ${taskNumber}...`);

            const fileExtension = audioBlob.type.split('/')[1] === 'mpeg' ? '.mp3' : '.m4a'; // Assuming mpeg for mp3, m4a for others
            const fileKey = `audio/task${taskNumber}_${generateId()}${fileExtension}`;

            const params = {
                Bucket: BUCKET_NAME,
                Key: fileKey,
                Body: audioBlob, // Directly pass Blob for web
                ContentType: audioBlob.type
            };

            return new Promise((resolve, reject) => {
                /* s3.upload(params, (err, data) => {
                    if (err) {
                        console.error(`❌ Error uploading task ${taskNumber}:`, err);
                        reject(err);
                    } else {
                        console.log(`✅ Upload successful task ${taskNumber}:`, data.Location);
                        resolve(data.Location);
                    }
                }); */

                resolve('');
            });

        } catch (error) {
            console.error(`❌ Error uploading audio task ${taskNumber}:`, error);
            throw error;
        }
    }, []);

    // Upload all audios to S3
    const uploadAudiosToS3 = useCallback(async (responses) => {
        try {
            console.log('📤 Starting upload to S3, responses received:', responses);
            setSubmissionStep('Préparation des fichiers audio...');

            const uploadPromises = [];
            const responsesWithAudio = responses.filter(response => response.userRecordingBlob);

            console.log('📁 Responses with audio to upload:', responsesWithAudio.length);

            if (responsesWithAudio.length === 0) {
                console.warn('⚠️ No responses with audio to upload found');
                return responses;
            }

            const updatedResponses = [...responses]; // Create a mutable copy

           /*  for (let i = 0; i < responsesWithAudio.length; i++) {
                const response = responsesWithAudio[i];
                if (response.userRecordingBlob) {
                    setSubmissionStep(`Upload de l'audio tâche ${response.taskNumber} (${i + 1}/${responsesWithAudio.length})...`);
                    try {
                        const uploadedUrl = await uploadSingleAudioToS3(response.userRecordingBlob, response.taskNumber);
                        // Find the original response in updatedResponses and update its audioUrl
                        const originalResponseIndex = updatedResponses.findIndex(r =>
                            r.taskNumber === response.taskNumber && r.timestamp === response.timestamp // Use timestamp for uniqueness
                        );
                        if (originalResponseIndex !== -1) {
                            updatedResponses[originalResponseIndex] = {
                                ...updatedResponses[originalResponseIndex],
                                audioUrl: uploadedUrl
                            };
                        }
                    } catch (error) {
                        console.error(`Error uploading audio for task ${response.taskNumber}:`, error);
                        // Continue with other uploads even if one fails
                        showToast(`Échec de l'upload pour la tâche ${response.taskNumber}.`, 'error');
                    }
                }
            } */

            setSubmissionStep('Mise à jour des réponses...');
            setCandidateResponses(updatedResponses); // Update state with uploaded URLs

            setSubmissionStep('Upload terminé avec succès !');
            showToast('Tous les enregistrements ont été uploadés avec succès !', 'success');
            console.log('✅ All responses with URLs:', updatedResponses);
            return updatedResponses;

        } catch (error) {
            console.error('❌ Error uploading audio:', error);
            setSubmissionStep('Erreur lors de l\'upload');
            showToast(`Impossible d'uploader les fichiers audio: ${error.message}. Veuillez réessayer.`, 'error');
            throw error;
        }
    }, [uploadSingleAudioToS3]);

    // Go back to selection screen with enhanced confirmation
    const goBack = useCallback(() => {
        showAlertDialog(
            'Quitter l\'examen',
            'Êtes-vous sûr de vouloir quitter ? Votre progression sera perdue.',
            [
                { text: 'Annuler', onPress: () => console.log('Exit cancelled') },
                {
                    text: 'Quitter',
                    onPress: () => {
                        cleanupResources();
                        navigation.goBack();
                    }
                }
            ]
        );
    }, [cleanupResources, navigation]);

    // --- Render Content ---
    const renderContent = () => {
        if (currentStep === 'introduction') {
            return (
                <div className={styles['exam-component-scroll-container']}>
                    <div className={styles['exam-component-scroll-content']}>
                        <div className={styles['exam-component-centered-container']} style={{ opacity: 1, transform: `translateY(0px)` }}> {/* Static styles for web-based animations */}
                            <div className={styles['exam-component-hero-container']}>
                                <div className={styles['exam-component-icon-container']}>
                                    <FontAwesomeIcon icon={faMicrophone} className={styles['exam-component-hero-icon']} />
                                    <div className={styles['exam-component-icon-glow']} />
                                </div>

                                <h2 className={styles['exam-component-hero-title']}>
                                    {type === 'complete' ? 'Examen Complet' : `Tâche ${type.slice(-1)}`}
                                </h2>
                                <p className={styles['exam-component-hero-subtitle']}>Expression Orale</p>

                                <div className={styles['exam-component-instruction-card']}>
                                    <FontAwesomeIcon icon={faInfoCircle} className={styles['exam-component-instruction-icon']} />
                                    <p className={styles['exam-component-instruction-text']}>
                                        Une introduction sera lue avant le début de l'épreuve. Vous pourrez l'interrompre à tout moment.
                                    </p>
                                </div>

                                <button
                                    onClick={startIntroduction}
                                    className={styles['exam-component-primary-button']}
                                >
                                    <span className={styles['exam-component-primary-button-text']}>
                                        Commencer l'examen
                                    </span>
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </button>

                                {canSkipIntro && (
                                    <button
                                        onClick={skipIntroduction}
                                        className={styles['exam-component-skip-button']}
                                    >
                                        <span className={styles['exam-component-skip-button-text']}>
                                            Passer l'introduction
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (currentStep === 'completed') {
            return (
                <div className={styles['exam-component-scroll-container']}>
                    <div className={styles['exam-component-scroll-content']}>
                        <div className={styles['exam-component-centered-container']}>
                            <div className={styles['exam-component-success-container']}>
                                <div className={styles['exam-component-success-icon-container']}>
                                    <FontAwesomeIcon icon={faCheckCircle} className={styles['exam-component-success-icon']} />
                                    <div className={styles['exam-component-success-glow']} />
                                </div>

                                <h2 className={styles['exam-component-success-title']}>
                                    Félicitations !
                                </h2>
                                <p className={styles['exam-component-success-subtitle']}>
                                    Votre examen a été soumis avec succès
                                </p>

                                <div className={styles['exam-component-completion-card']}>
                                    <div className={styles['exam-component-completion-stats']}>
                                        <div className={styles['exam-component-stat-item']}>
                                            <FontAwesomeIcon icon={faClock} className={styles['exam-component-stat-icon']} />
                                            <p className={styles['exam-component-stat-label']}>Durée totale</p>
                                            <p className={styles['exam-component-stat-value']}>
                                                {type === 'complete' ? '~10 min' : '~3 min'}
                                            </p>
                                        </div>
                                        <div className={styles['exam-component-stat-item']}>
                                            <FontAwesomeIcon icon={faMicrophone} className={styles['exam-component-stat-icon']} />
                                            <p className={styles['exam-component-stat-label']}>Enregistrements</p>
                                            <p className={styles['exam-component-stat-value']}>
                                                {candidateResponses.length}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => navigation.goBack()}
                                    className={styles['exam-component-success-button']}
                                >
                                    <span className={styles['exam-component-success-button-text']}>
                                        Terminer
                                    </span>
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Display during exam (playing audio, preparing, recording, waiting)
        return (
            <div className={styles['exam-component-main-container']}>
                {/* Header with progress */}
                <header className={styles['exam-component-exam-header']}>
                    <button onClick={goBack} className={styles['exam-component-back-button']}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>

                    <div className={styles['exam-component-task-info']}>
                        <p className={styles['exam-component-task-number']}>
                            {currentTask === 'task1' ? 'Tâche 1' :
                                currentTask === 'task2' ? 'Tâche 2' :
                                    'Tâche 3'}
                        </p>
                        <p className={styles['exam-component-task-name']}>
                            {currentTask === 'task1' ? 'Entretien dirigé' :
                                currentTask === 'task2' ? 'Exercice interactif' :
                                    'Expression d\'un point de vue'}
                        </p>
                    </div>

                    {type === 'complete' && (
                        <div className={styles['exam-component-progress-container']}>
                            <div className={styles['exam-component-progress-bar-background']}>
                                <div
                                    className={styles['exam-component-progress-bar-fill']}
                                    style={{ width: `${taskProgress}%` }}
                                />
                            </div>
                            <p className={styles['exam-component-progress-text']}>{Math.round(taskProgress)}%</p>
                        </div>
                    )}
                </header>

                {/* Main content area for exam steps */}
                <div className={styles['exam-component-exam-content-scroll']}>
                    <div className={styles['exam-component-exam-content-container']}>
                        <div className={styles['exam-component-exam-content']}>
                            {isAudioPlaying && (
                                <div className={styles['exam-component-audio-playing-card']}>
                                    <div className={styles['exam-component-audio-icon']}>
                                        <FontAwesomeIcon icon={faVolumeUp} />
                                    </div>
                                    <div className={styles['exam-component-wave-container']}>
                                        {[...Array(5)].map((_, i) => (
                                            <div
                                                key={i}
                                                className={styles['exam-component-wave-bars']}
                                                style={{ animationDelay: `${i * 0.1}s` }} // Stagger animation
                                            />
                                        ))}
                                    </div>
                                    <p className={styles['exam-component-audio-status-text']}>
                                        Écoute de la consigne en cours...
                                    </p>
                                    <p className={styles['exam-component-audio-hint']}>
                                        Restez attentif, l'enregistrement commencera automatiquement
                                    </p>
                                </div>
                            )}

                            {isPreparing && (
                                <div className={styles['exam-component-preparation-card']}>
                                    <div className={styles['exam-component-preparation-icon']}>
                                        <FontAwesomeIcon icon={faClock} />
                                    </div>
                                    <p className={styles['exam-component-preparation-title']}>
                                        Temps de préparation
                                    </p>
                                    <p className={styles['exam-component-timer-display']}>
                                        {formatTime(timeRemaining)}
                                    </p>
                                    <p className={styles['exam-component-preparation-hint']}>
                                        Préparez votre réponse, l'enregistrement commencera automatiquement
                                    </p>

                                    <button
                                        onClick={skipPreparation}
                                        className={styles['exam-component-skip-preparation-button']}
                                    >
                                        <FontAwesomeIcon icon={faArrowRight} />
                                        <span className={styles['exam-component-skip-preparation-text']}>
                                            Commencer l'enregistrement
                                        </span>
                                    </button>
                                </div>
                            )}

                            {isRecording && (
                                <div className={styles['exam-component-recording-card']}>
                                    <div className={`${styles['exam-component-recording-icon']} ${styles['pulsing']}`}>
                                        <FontAwesomeIcon icon={faMicrophone} />
                                    </div>

                                    <p className={styles['exam-component-recording-title']}>
                                        Enregistrement en cours
                                    </p>

                                    <div className={styles['exam-component-timer-container']}>
                                        <p className={styles['exam-component-timer-display']}>
                                            {formatTime(timeRemaining)}
                                        </p>
                                        <div className={styles['exam-component-progress-ring']}>
                                            <div
                                                className={styles['exam-component-progress-ring-fill']}
                                                style={{
                                                    transform: `rotate(${((taskDurations[currentTask] - timeRemaining) / taskDurations[currentTask]) * 360}deg)`
                                                }}
                                            />
                                            {/* Static circle for progress ring background */}
                                            <div className={styles['exam-component-progress-ring-background-circle']}></div>
                                        </div>
                                    </div>

                                    <div className={styles['exam-component-recording-actions']}>
                                        <button
                                            onClick={stopRecording}
                                            className={styles['exam-component-stop-recording-button']}
                                        >
                                            <FontAwesomeIcon icon={faStop} />
                                            <span className={styles['exam-component-stop-recording-text']}>
                                                Arrêter l'enregistrement
                                            </span>
                                        </button>
                                    </div>

                                    <p className={styles['exam-component-recording-hint']}>
                                        Parlez clairement dans le microphone
                                    </p>
                                </div>
                            )}

                            {!isAudioPlaying && !isPreparing && !isRecording && currentStep !== 'introduction' && currentStep !== 'completed' && (
                                <div className={styles['exam-component-waiting-card']}>
                                    <div className={styles['exam-component-waiting-icon']}>
                                        <FontAwesomeIcon icon={faSpinner} className={styles['exam-component-spinner']} />
                                    </div>
                                    <p className={styles['exam-component-waiting-title']}>
                                        Préparation en cours
                                    </p>
                                    <p className={styles['exam-component-waiting-text']}>
                                        Veuillez patienter pendant que nous préparons la prochaine étape...
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles['exam-component-container']}>
            {/* Using div as SafeAreaView and managing styles via CSS */}
            {renderContent()}

            {/* Submission Modal */}
            {isSubmitting && (
                <div className={styles['exam-component-modal-overlay']}>
                    <div className={styles['exam-component-modal-content']}>
                        <div className={styles['exam-component-modal-icon-container']}>
                            <FontAwesomeIcon icon={faSpinner} className={styles['exam-component-spinner-large']} />
                            <div className={styles['exam-component-modal-icon-glow']} />
                        </div>

                        <h3 className={styles['exam-component-modal-title']}>Finalisation en cours</h3>
                        <p className={styles['exam-component-modal-description']}>
                            Nous sauvegardons vos enregistrements de manière sécurisée.
                            Cette opération peut prendre quelques instants.
                        </p>

                        <div className={styles['exam-component-modal-progress-container']}>
                            <p className={styles['exam-component-modal-step-text']}>{submissionStep}</p>
                            <div className={styles['exam-component-modal-progress-bar']}>
                                <div className={styles['exam-component-modal-progress-fill']} />
                            </div>
                        </div>

                        <p className={styles['exam-component-modal-note']}>
                            Veuillez ne pas fermer cette page
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamScreenExpressionOrale;